import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DIRECTION_NOTE, direction, num, pct, stateLabel, stateNote } from '../format';
import Flag from './Flag';
import Logo from './Logo';

/* One trend, as a card. Shared by the signed-in grid and the public preview so
 * the preview is the product rather than a drawing of it - a mock would drift
 * the first time this changed, and a visitor comparing the two would be right
 * to trust neither.
 *
 * locked keeps everything that shows a trend is real and moving - the curve,
 * the change, how long it has run - and withholds only the two things that
 * identify it. Blurring the whole card would say nothing; blurring the name
 * says there is something here and you cannot see what. */

export function Sparkline({ points, up }) {
  const path = useMemo(() => {
    const vals = (points || [])
      .map((p) => p.value)
      .filter((v) => v !== null && v !== undefined)
      .map(Number);
    if (vals.length < 2) return null;
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const span = hi - lo || 1;
    const step = 100 / (vals.length - 1);
    return vals
      .map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(2)},${
        (26 - ((v - lo) / span) * 24).toFixed(2)}`)
      .join(' ');
  }, [points]);

  if (!path) return <div className="tw-spark is-empty" />;
  return (
    <svg className="tw-spark" viewBox="0 0 100 28" preserveAspectRatio="none" aria-hidden="true">
      <path d={path} fill="none" stroke="currentColor" strokeWidth="1.4"
        vectorEffect="non-scaling-stroke" className={up ? 'up' : 'down'} />
    </svg>
  );
}

export default function TrendTile({ signal: s, points, locked = false }) {
  const dir = direction(s.sentiment_score);
  /* The grid's filter lives in its query string, so the way back has to carry
   * it. Only from the grid itself: this tile also renders on the preview,
   * where the search belongs to a different page and would send a reader to a
   * list they never chose. */
  const { pathname, search } = useLocation();
  const list = pathname === '/trends' && search ? search : null;
  const body = (
    <>
      <div className="tw-tile-head">
        {locked ? (
          <span className="tw-logo is-mono tw-hidden-chip" aria-hidden="true" />
        ) : (
          <Logo symbol={s.eodhd_symbol} name={s.company} ticker={s.ticker} size={30} />
        )}
        <div className="tw-tile-id">
          {locked ? (
            <>
              <span className="tw-hidden-bar" style={{ width: '62%' }} />
              <span className="tw-hidden-bar sm" style={{ width: '44%' }} />
            </>
          ) : (
            <>
              <span className="tw-tile-term">{s.trend_term}</span>
              <span className="tw-tile-co">
                <span className="tw-ticker">{s.ticker}</span>
                {/* The name needs an element of its own to be truncated in.
                  * .tw-tile-co is a flex row, so the bare text beside the
                  * ticker was an anonymous flex item, and text-overflow does
                  * not reach those - long names were cut mid-letter with no
                  * ellipsis to show it had happened. */}
                <span className="tw-tile-coname">{s.company}</span>
              </span>
            </>
          )}
        </div>
        {!locked && <Flag geo={s.geo} size={15} />}
      </div>

      <Sparkline points={points} up={s.pct_change >= 0} />

      <div className="tw-tile-foot">
        <span className={`tw-num ${s.pct_change > 0 ? 'up' : s.pct_change < 0 ? 'down' : 'mute'}`}>
          {pct(s.pct_change)}
        </span>
        <span className={`tw-state ${s.state}`} title={stateNote(s.state) || undefined}>
          {stateLabel(s.state)} · {s.weeks_open ?? 0}w
        </span>
        {s.materiality_score !== null && s.materiality_score !== undefined && (
          <span className="tw-tile-mat">materiality {num(s.materiality_score)}</span>
        )}
        {/* Against materiality rather than beside the percentage on the left,
          * because it qualifies that figure and not the curve. Two green
          * numbers at opposite ends of the row would read as one measurement
          * printed twice. */}
        {dir && (
          <span className={`tw-dir ${dir.cls}`}
                title={`${dir.word} ${dir.label}. ${DIRECTION_NOTE}`}>
            {dir.arrow}{Math.abs(dir.n)}
          </span>
        )}
      </div>
    </>
  );

  // A locked tile is not a link. Clicking it would bounce to sign-in, which
  // reads as a broken page rather than an invitation - the panel below does the
  // asking instead.
  if (locked) return <div className="tw-tile is-locked">{body}</div>;

  return (
    <Link className="tw-tile" to={`/trends/${s.episode_id}`} state={list ? { list } : undefined}>
      {body}
    </Link>
  );
}
